import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiExternalLink,
  FiGithub,
  FiSearch,
} from "react-icons/fi";
import { projectsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    project: null,
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const filtered = projects.filter(
      (project) =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.technologies.some((tech) =>
          tech.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );
    setFilteredProjects(filtered);
  }, [searchTerm, projects]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getProjects();
      setProjects(response.data);
      setFilteredProjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      await projectsAPI.deleteProject(projectId);
      setProjects(projects.filter((p) => p._id !== projectId));
      toast.success("Project deleted successfully");
      setDeleteModal({ isOpen: false, project: null });
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const toggleFeatured = async (project) => {
    try {
      const formData = new FormData();
      formData.append("title", project.title);
      formData.append("description", project.description);
      formData.append("technologies", JSON.stringify(project.technologies));
      formData.append("liveUrl", project.liveUrl || "");
      formData.append("githubUrl", project.githubUrl || "");
      formData.append("featured", !project.featured);
      formData.append("existingImages", JSON.stringify(project.images));

      await projectsAPI.updateProject(project._id, formData);
      fetchProjects(); // Refresh the list
      toast.success(`Project ${!project.featured ? "featured" : "unfeatured"}`);
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="text-gray-600">Manage your portfolio projects</p>
        </div>
        <Link to="/projects/new" className="btn btn-primary">
          <FiPlus className="mr-2" size={16} />
          Add Project
        </Link>
      </div>

      {/* Search */}
      <div className="card p-4">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input pl-10"
          />
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project._id} className="card overflow-hidden">
            {/* Project Image */}
            {project.images?.[0] && (
              <div className="relative h-48">
                <img
                  src={project.images?.[0]?.url || "/placeholder.png"} // Cloudinary URL
                  alt={project.title}
                  className="w-full h-full object-cover"
                />

                {project.featured && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Project Content */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {project.title}
              </h3>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-1 mb-4">
                {project.technologies.slice(0, 3).map((tech, index) => (
                  <span
                    key={index}
                    className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                  >
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 3 && (
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                    +{project.technologies.length - 3}
                  </span>
                )}
              </div>

              {/* External Links */}
              <div className="flex items-center space-x-3 mb-4">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                    title="Live Demo"
                  >
                    <FiExternalLink size={16} />
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-gray-800"
                    title="GitHub"
                  >
                    <FiGithub size={16} />
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <Link
                    to={`/projects/edit/${project._id}`}
                    className="btn btn-secondary !py-1 !px-2 !text-sm"
                  >
                    <FiEdit size={14} className="mr-1" />
                    Edit
                  </Link>
                  <button
                    onClick={() => setDeleteModal({ isOpen: true, project })}
                    className="btn btn-danger !py-1 !px-2 !text-sm"
                  >
                    <FiTrash2 size={14} className="mr-1" />
                    Delete
                  </button>
                </div>
                <button
                  onClick={() => toggleFeatured(project)}
                  className={`text-xs px-2 py-1 rounded-full border ${
                    project.featured
                      ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                      : "bg-gray-100 text-gray-600 border-gray-300"
                  }`}
                >
                  {project.featured ? "Unfeature" : "Feature"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && !loading && (
        <div className="text-center py-12">
          <FiEye className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No projects found
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? "Try adjusting your search terms"
              : "Get started by creating your first project"}
          </p>
          {!searchTerm && (
            <Link to="/projects/new" className="btn btn-primary">
              <FiPlus className="mr-2" size={16} />
              Add Project
            </Link>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, project: null })}
        title="Delete Project"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{deleteModal.project?.title}"? This
            action cannot be undone.
          </p>
          <div className="flex space-x-3 justify-end">
            <button
              onClick={() => setDeleteModal({ isOpen: false, project: null })}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteModal.project._id)}
              className="btn btn-danger"
            >
              Delete Project
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Projects;
